/**
 * ErrorBoundary - React error boundary component
 * Catches JavaScript errors in child components and displays fallback UI
 * Based on React docs and BurrowTV-LITE template patterns
 */

import React from 'react';
import { Box, Heading, Text, Button, VStack } from '@chakra-ui/react';
import ErrorHandler from '../utils/ErrorHandler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to ErrorHandler
    ErrorHandler.handleError(
      'ErrorBoundary',
      error,
      null,
      {
        silent: true, // Don't show toast since we're showing fallback UI
        logToConsole: true
      }
    );
    
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Optionally reload the page or navigate home
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.900"
          color="white"
          px={4}
        >
          <VStack spacing={6} maxW="600px" textAlign="center">
            <Heading size="2xl">Oops!</Heading>
            <Text fontSize="lg" color="gray.300">
              Something went wrong. We've logged the error and will look into it.
            </Text>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                bg="red.900"
                color="red.100"
                p={4}
                borderRadius="md"
                textAlign="left"
                w="100%"
                maxH="300px"
                overflowY="auto"
                fontSize="sm"
              >
                <Text fontWeight="bold" mb={2}>
                  {this.state.error.toString()}
                </Text>
                <Text as="pre" fontSize="xs" whiteSpace="pre-wrap">
                  {this.state.errorInfo?.componentStack}
                </Text>
              </Box>
            )}
            
            <VStack spacing={3}>
              <Button
                colorScheme="blue"
                size="lg"
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </VStack>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
